import { useState, useRef, useCallback } from 'react'
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  Collapse,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
} from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import arbolEc from '../../../assets/images/logo-app-white.png'
import * as RT from '../../../config/routeType'
import { MAIN as MAIN_ROUTE, LOGIN_ROUTE, PROFILE_ROUTE } from '../../../config/routes'
import { useCities } from '../../../context/CitiesContext'

const HeaderComponent = ({ isAuth }) => {
  const trigger = useScrollTrigger({ threshold: 100 })
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [mobileMapaOpen, setMobileMapaOpen] = useState(false)
  const navigate = useNavigate()
  const { cantones, loading: cantonesLoading } = useCities()

  // Submenú "Ir a Mapa" — hover
  const [anchorElMapa, setAnchorElMapa] = useState(null)
  const mapaCloseTimeout = useRef(null)
  const openMapa = Boolean(anchorElMapa)

  const handleOpenMapa = useCallback(event => {
    clearTimeout(mapaCloseTimeout.current)
    setAnchorElMapa(event.currentTarget)
  }, [])

  const handleStartCloseMapa = useCallback(() => {
    clearTimeout(mapaCloseTimeout.current)
    mapaCloseTimeout.current = setTimeout(() => setAnchorElMapa(null), 220)
  }, [])

  const handleCancelCloseMapa = useCallback(() => {
    clearTimeout(mapaCloseTimeout.current)
  }, [])

  const handleCloseMapa = useCallback(() => {
    clearTimeout(mapaCloseTimeout.current)
    setAnchorElMapa(null)
  }, [])

  // Submenú "Nosotros" — click
  const [anchorElNosotros, setAnchorElNosotros] = useState(null)
  const openNosotros = Boolean(anchorElNosotros)
  const handleOpenNosotros = event => setAnchorElNosotros(event.currentTarget)
  const handleCloseNosotros = () => setAnchorElNosotros(null)

  const handleLogoClick = () => {
    window.location.href = MAIN_ROUTE
  }

  const handleCantonSelect = useCallback(slug => {
    handleCloseMapa()
    navigate(`/${slug}`)
  }, [handleCloseMapa, navigate])

  const nosotrosSubmenu = [
    { text: 'Equipo de trabajo', path: `/${RT.TEAM}` },
    { text: 'Publicaciones', path: `/${RT.PUBLICATIONS}` },
  ]

  const staticNavLinks = [
    {
      text: 'Nosotros',
      submenu: nosotrosSubmenu,
      key: 'nosotros',
    },
    { text: 'Noticias', path: `/${RT.NEWS}`, key: 'noticias' },
    { text: 'Contacto', path: `/${RT.CONTACT}`, key: 'contacto' },
    { text: 'Iniciar Sesión', path: LOGIN_ROUTE, flag: true, key: 'auth' },
  ]

  return (
    <>
      <AppBar
        position="fixed"
        elevation={trigger ? 4 : 0}
        sx={{
          background: !trigger
            ? '#011d41'
            : 'linear-gradient(to bottom, rgba(12, 35, 66),  rgba(12, 35, 66, 0.6), rgba(12, 35, 66, 0.4))',
          backdropFilter: !trigger ? 'blur(0px)' : 'blur(4px)',
          color: '#fff',
          transition: 'all 0.4s',
          zIndex: 4,
          py: 1,
        }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box
            onClick={handleLogoClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              cursor: 'pointer',
            }}>
            <Box component="img" src={arbolEc} alt="logo" sx={{ maxHeight: 38, mr: 1 }} />
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Ir a Mapa — hover dropdown con cantones */}
              <Box onMouseLeave={handleStartCloseMapa}>
                <Button
                  onMouseEnter={handleOpenMapa}
                  sx={{
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { color: theme.palette.secondary.main },
                  }}
                  endIcon={<ExpandMoreIcon />}>
                  Ir a Mapa
                </Button>
                <Menu
                  anchorEl={anchorElMapa}
                  open={openMapa}
                  onClose={handleCloseMapa}
                  disableScrollLock
                  disableAutoFocusItem
                  transitionDuration={{ enter: 120, exit: 100 }}
                  MenuListProps={{
                    onMouseEnter: handleCancelCloseMapa,
                    onMouseLeave: handleStartCloseMapa,
                    sx: { py: 0.5 },
                  }}
                  PaperProps={{
                    elevation: 3,
                    sx: { minWidth: 160, mt: 0.5 },
                  }}>
                  {!cantonesLoading && cantones.length === 0 && (
                    <MenuItem disabled sx={{ fontStyle: 'italic', fontSize: 14 }}>
                      Sin cantones activos
                    </MenuItem>
                  )}
                  {cantones.map(canton => (
                    <MenuItem
                      key={canton.slug}
                      onClick={() => handleCantonSelect(canton.slug)}
                      sx={{
                        fontWeight: 500,
                        gap: 1,
                        '&:hover': { color: theme.palette.primary.main },
                      }}>
                      <MapOutlinedIcon fontSize="small" sx={{ opacity: 0.6 }} />
                      {canton.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>

              {/* Nosotros */}
              <Box>
                <Button
                  onClick={handleOpenNosotros}
                  sx={{
                    color: '#fff',
                    fontSize: 16,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': { color: theme.palette.secondary.main },
                  }}
                  endIcon={<ExpandMoreIcon />}>
                  Nosotros
                </Button>
                <Menu
                  anchorEl={anchorElNosotros}
                  open={openNosotros}
                  disableScrollLock
                  onClose={handleCloseNosotros}>
                  {nosotrosSubmenu.map(({ text: subText, path: subPath }) => (
                    <MenuItem
                      key={subText}
                      component={RouterLink}
                      to={subPath}
                      onClick={handleCloseNosotros}
                      sx={{ fontWeight: 500 }}>
                      {subText}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>

              {/* Links estáticos */}
              {staticNavLinks
                .filter(l => !l.submenu)
                .map(({ text, path, flag, key }) => (
                  <Button
                    key={key}
                    component={RouterLink}
                    to={isAuth && Boolean(flag) ? PROFILE_ROUTE : path}
                    sx={{
                      color: '#fff',
                      fontSize: 16,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { color: theme.palette.secondary.main },
                    }}>
                    {isAuth && Boolean(flag) ? 'Perfil' : text}
                  </Button>
                ))}
            </Box>
          )}

          {/* Mobile Toggle */}
          {isMobile && (
            <IconButton edge="end" color="inherit" onClick={() => setDrawerOpen(true)}>
              <MenuIcon fontSize="large" />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer Mobile */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <List sx={{ width: 250 }}>
          {/* Ir a Mapa — expandible */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => setMobileMapaOpen(prev => !prev)}
              sx={{ fontWeight: 600, justifyContent: 'space-between' }}>
              Ir a Mapa
              {mobileMapaOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>
          </ListItem>
          <Collapse in={mobileMapaOpen} timeout="auto" unmountOnExit>
            <List disablePadding>
              {cantones.map(canton => (
                <ListItem key={canton.slug} disablePadding>
                  <ListItemButton
                    sx={{ pl: 4, fontWeight: 500 }}
                    onClick={() => {
                      setDrawerOpen(false)
                      setMobileMapaOpen(false)
                      navigate(`/${canton.slug}`)
                    }}>
                    {canton.name}
                  </ListItemButton>
                </ListItem>
              ))}
              {cantones.length === 0 && (
                <ListItem>
                  <ListItemButton disabled sx={{ pl: 4, fontStyle: 'italic', fontSize: 14 }}>
                    Sin cantones activos
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Collapse>

          {/* Nosotros */}
          {nosotrosSubmenu.map(({ text: subText, path: subPath }) => (
            <ListItem key={subText} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={subPath}
                onClick={() => setDrawerOpen(false)}
                sx={{ fontWeight: 600 }}>
                {subText}
              </ListItemButton>
            </ListItem>
          ))}

          {/* Links estáticos */}
          {staticNavLinks
            .filter(l => !l.submenu)
            .map(({ text, path, flag, key }) => (
              <ListItem key={key} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={isAuth && Boolean(flag) ? PROFILE_ROUTE : path}
                  onClick={() => setDrawerOpen(false)}
                  sx={{ fontWeight: 600 }}>
                  {isAuth && Boolean(flag) ? 'Perfil' : text}
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Drawer>
    </>
  )
}

export default HeaderComponent
